/**
 * LocalDealsHero — "Your Local Deals Today"
 *
 * A prominent strip that surfaces active deals for the shopper's
 * location. Radius chips: 1 / 5 / 10 / 25 / 50 / 100 km, state,
 * national, online. Falls back to a "Pick your city" state when no
 * location has been resolved yet.
 *
 * Data:
 *   GET /api/deals/nearby?lat=&lon=&radius_km=  → local
 *   GET /api/deals?limit=&kind=offer            → national fallback
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Tag, Percent, Timer, Store, TrendingDown, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import LocationPicker, { getStoredLocation, saveStoredLocation } from "@/components/LocationPicker";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";
const RED   = "#7B2A2E";

const RADIUS_OPTIONS = [
  { key: "1",   label: "1 km",   km: 1 },
  { key: "5",   label: "5 km",   km: 5 },
  { key: "10",  label: "10 km",  km: 10 },
  { key: "25",  label: "25 km",  km: 25 },
  { key: "50",  label: "50 km",  km: 50 },
  { key: "100", label: "100 km", km: 100 },
  { key: "state",    label: "State",    km: null },
  { key: "national", label: "National", km: null },
  { key: "online",   label: "Online",   km: null },
];

export default function LocalDealsHero({ limit = 12 }) {
  const [location, setLocation] = useState(getStoredLocation());
  const [radiusKey, setRadiusKey] = useState("25");
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const opt = RADIUS_OPTIONS.find((r) => r.key === radiusKey);
      let items = [];
      if (opt?.km && location?.latitude && location?.longitude) {
        const r = await http.get("/deals/nearby", {
          params: { lat: location.latitude, lon: location.longitude, radius_km: opt.km, limit },
        });
        items = r.data?.items || [];
      } else if (radiusKey === "state" && location?.state) {
        const r = await http.get("/deals", { params: { state: location.state, limit } });
        items = r.data?.items || r.data || [];
      } else {
        // national / online / no-location fallback
        const r = await http.get("/deals", { params: { limit, kind: "offer" } });
        items = r.data?.items || r.data || [];
      }
      setDeals(items);
    } catch { setDeals([]); }
    setLoading(false);
  }, [radiusKey, location, limit]);

  useEffect(() => { load(); }, [load]);

  // Sync with the global FoodPulse location bar so any change there
  // (auto-detect, manual pick, re-detect) instantly reloads deals here.
  useEffect(() => {
    const onFpLoc = (e) => {
      const l = e.detail || getStoredLocation();
      if (!l) return;
      setLocation({
        city: l.city,
        formatted_address: l.formatted_address || [l.city, l.state, l.country].filter(Boolean).join(", "),
        latitude: l.lat || l.latitude,
        longitude: l.lon || l.longitude,
        state: l.state, country: l.country,
      });
    };
    window.addEventListener("fp:location:change", onFpLoc);
    return () => window.removeEventListener("fp:location:change", onFpLoc);
  }, []);

  const onLocation = (loc) => {
    setLocation(loc);
    saveStoredLocation(loc);
    setShowPicker(false);
  };

  return (
    <section className="w-full" data-testid="local-deals-hero"
             style={{ background: `linear-gradient(180deg, ${CREAM} 0%, #FFF7E4 100%)` }}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>
              — Your local deals today
            </div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: GREEN }}>
              Save real money on real food{location?.city ? ` in ${location.city}` : ""}
            </h2>
          </div>
          <div className="flex items-center gap-2 min-w-[240px] max-w-[380px] flex-1">
            {location && !showPicker ? (
              <button onClick={() => setShowPicker(true)}
                      className="flex items-center gap-2 rounded-full border-2 bg-white px-3 py-2 text-sm hover:bg-amber-50"
                      style={{ borderColor: "#E9D8B4", color: GREEN }}
                      data-testid="deals-location-open">
                <MapPin className="w-4 h-4" />
                <span className="font-semibold truncate">{location.city || location.formatted_address}</span>
                <span className="opacity-50 text-xs ml-1">change</span>
              </button>
            ) : (
              <LocationPicker value={location} onChange={onLocation} />
            )}
          </div>
        </div>

        {/* Radius chips */}
        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="deals-radius-chips">
          {RADIUS_OPTIONS.map((r) => (
            <button key={r.key} onClick={() => setRadiusKey(r.key)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold border-2 transition`}
                    style={{
                      background:  radiusKey === r.key ? GREEN : "transparent",
                      color:       radiusKey === r.key ? "white" : GREEN,
                      borderColor: radiusKey === r.key ? GREEN : "#E9D8B4",
                    }}
                    data-testid={`deals-radius-${r.key}`}>
              {r.label}
            </button>
          ))}
        </div>

        {/* Deals grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin opacity-50" /></div>
        ) : deals.length === 0 ? (
          <EmptyState location={location} radius={radiusKey} onOpen={() => setShowPicker(true)} />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {deals.slice(0, limit).map((d) => <DealCard key={d.id || d.slug} deal={d} />)}
            </div>
            <div className="mt-5 text-right">
              <Link to="/food-pulse/deals" className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-white"
                    style={{ background: GREEN }} data-testid="deals-see-all">
                See all deals <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function DealCard({ deal }) {
  const pct = deal.discount_pct
    ?? (deal.original_price && deal.deal_price
        ? Math.round(100 - (deal.deal_price / deal.original_price) * 100)
        : null);
  return (
    <Link to={`/food-pulse/deals/${deal.slug}`}
          className="group rounded-2xl border-2 bg-white p-3 hover:shadow-lg transition"
          style={{ borderColor: "#E9D8B4" }}
          data-testid={`deal-card-${deal.slug || deal.id}`}>
      <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-2 bg-amber-50">
        {deal.image_urls?.[0]
          ? <img src={deal.image_urls[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          : <div className="w-full h-full flex items-center justify-center opacity-40"><Tag className="w-8 h-8" /></div>}
        {pct != null && (
          <div className="absolute top-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow"
               style={{ background: RED }}>
            −{pct}%
          </div>
        )}
        {deal.distance_km != null && (
          <div className="absolute bottom-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-semibold flex items-center gap-1 bg-white/95 shadow"
               style={{ color: GREEN }}>
            <MapPin className="w-3 h-3" /> {deal.distance_km} km
          </div>
        )}
      </div>
      <div className="text-xs font-semibold truncate" style={{ color: GREEN }}>{deal.title}</div>
      <div className="flex items-baseline gap-1.5 mt-1">
        {deal.deal_price != null && (
          <div className="font-display text-lg font-bold" style={{ color: RED }}>${deal.deal_price}</div>
        )}
        {deal.original_price != null && deal.original_price !== deal.deal_price && (
          <div className="text-xs line-through opacity-50">${deal.original_price}</div>
        )}
        {deal.unit && <div className="text-[10px] opacity-60">/ {deal.unit}</div>}
      </div>
      <div className="mt-1 text-[11px] opacity-70 truncate flex items-center gap-1">
        <Store className="w-3 h-3 flex-shrink-0" /> {deal.vendor_name || deal.city}
      </div>
    </Link>
  );
}

function EmptyState({ location, radius, onOpen }) {
  const needLoc = !location && ["1","5","10","25","50","100"].includes(radius);
  return (
    <div className="rounded-3xl border-2 border-dashed p-10 text-center" style={{ borderColor: AMBER, background: "#FFF7E4" }}>
      {needLoc ? (
        <>
          <MapPin className="w-10 h-10 mx-auto opacity-40 mb-2" />
          <div className="font-display text-xl mb-1" style={{ color: GREEN }}>Tell us where you are</div>
          <div className="text-sm opacity-70 mb-4">We'll surface today's deals within your chosen radius.</div>
          <button onClick={onOpen} className="rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: GREEN }}>
            Pick my city
          </button>
        </>
      ) : (
        <>
          <TrendingDown className="w-10 h-10 mx-auto opacity-40 mb-2" />
          <div className="font-display text-xl mb-1" style={{ color: GREEN }}>No live deals yet in this range</div>
          <div className="text-sm opacity-70 mb-4">Try a wider radius, or check back tomorrow — vendors publish daily.</div>
          <Link to="/food-pulse/spot" className="rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: AMBER, color: GREEN }}>
            Spotted a deal? Share it →
          </Link>
        </>
      )}
    </div>
  );
}
